
import { useEffect, useRef, useState } from "react";
import { Mail, Phone, MapPin, Send, Calendar, CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export const Contact = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    const elements = sectionRef.current?.querySelectorAll('.animate-on-scroll, .animate-on-scroll-left, .animate-on-scroll-right');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    await new Promise(resolve => setTimeout(resolve, 2000));

    setIsSubmitting(false);
    setIsSubmitted(true);

    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', email: '', company: '', message: '' });
    }, 3000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const benefits = [
    "Senior professionals on every project",
    "Deep expertise in enterprise systems",
    "Client-focused approach",
    "Clear communication throughout",
    "We build solutions, not presentations",
    "Hands-on support when you need it"
  ];

  return (
    <section ref={sectionRef} id="contact" className="py-32 relative overflow-hidden noise-overlay">
      {/* Background */}
      <div className="absolute inset-0 bg-background" />
      <div className="absolute inset-0 mesh-gradient-1 opacity-40" />

      {/* Decorative Elements */}
      <div className="absolute top-20 left-0 w-[500px] h-[500px] bg-amber-500/5 rounded-full blur-[150px]" />
      <div className="absolute bottom-20 right-0 w-[400px] h-[400px] bg-cyan-500/5 rounded-full blur-[120px]" />

      {/* Grid Pattern */}
      <div className="absolute inset-0 grid-pattern opacity-20" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header - Left Aligned */}
        <div className="max-w-2xl mb-16">
          <div className="animate-on-scroll">
            <span className="label-mono text-amber-400 tracking-widest">
              Get Started
            </span>
          </div>

          <h2 className="mt-6 display-lg text-white animate-on-scroll stagger-1">
            Let's Build Something
            <span className="text-gradient-gold block">Amazing</span>
          </h2>

          <p className="mt-6 text-xl text-zinc-400 leading-relaxed animate-on-scroll stagger-2">
            Work with experienced professionals who deliver real results.
            <span className="text-white font-medium"> ERP • eCommerce • Integrations</span>
          </p>

          {/* Trust Badge */}
          <div className="mt-8 animate-on-scroll stagger-3">
            <div className="inline-flex items-center gap-3 glass-panel-warm rounded-full px-5 py-3">
              <div className="relative">
                <div className="w-2 h-2 bg-emerald-400 rounded-full" />
                <div className="absolute inset-0 w-2 h-2 bg-emerald-400 rounded-full animate-ping" />
              </div>
              <span className="text-sm text-zinc-300">
                We'll connect you with active clients first —
                <span className="text-amber-400 font-medium"> start only when confident</span>
              </span>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Contact Form */}
          <div className="lg:col-span-7">
            <div className="card-featured p-8 md:p-10 animate-on-scroll-left stagger-4">
              <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent" />

              <h3 className="text-2xl font-bold text-white mb-8">Start Your Project</h3>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-zinc-400">Your Name</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      required
                      className="h-12 bg-zinc-900/50 border-white/10 text-white placeholder:text-zinc-600 focus:border-amber-500/50 focus:ring-amber-500/20 rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-zinc-400">Email Address</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@company.com"
                      required
                      className="h-12 bg-zinc-900/50 border-white/10 text-white placeholder:text-zinc-600 focus:border-amber-500/50 focus:ring-amber-500/20 rounded-xl"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-zinc-400">Company <span className="text-zinc-600">(Optional)</span></label>
                  <Input
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Your Company"
                    className="h-12 bg-zinc-900/50 border-white/10 text-white placeholder:text-zinc-600 focus:border-amber-500/50 focus:ring-amber-500/20 rounded-xl"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-zinc-400">Project Details</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project requirements, timeline, and goals..."
                    rows={5}
                    required
                    className="bg-zinc-900/50 border-white/10 text-white placeholder:text-zinc-600 resize-none focus:border-amber-500/50 focus:ring-amber-500/20 rounded-xl"
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting || isSubmitted}
                  className={`w-full h-14 rounded-xl text-lg font-semibold transition-all duration-300 group ${
                    isSubmitted
                      ? 'bg-emerald-500 text-white'
                      : 'btn-primary'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin mr-2" />
                      Sending...
                    </>
                  ) : isSubmitted ? (
                    <>
                      <CheckCircle2 className="w-5 h-5 mr-2" />
                      Message Sent!
                    </>
                  ) : (
                    <>
                      Send Message
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </Button>
              </form>

              {/* Quick Actions */}
              <div className="mt-8 pt-8 border-t border-white/5">
                <p className="text-sm text-zinc-500 mb-4">Prefer to talk directly?</p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 h-12 border-white/10 text-zinc-300 hover:bg-white/5 hover:text-white hover:border-amber-500/30 rounded-xl"
                    onClick={() => window.open('tel:+14242424140', '_self')}
                  >
                    <Phone className="mr-2 w-4 h-4" />
                    Call Now
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 h-12 border-white/10 text-zinc-300 hover:bg-white/5 hover:text-white hover:border-amber-500/30 rounded-xl"
                    onClick={() => window.open('https://calendly.com/fortifytek', '_blank')}
                  >
                    <Calendar className="mr-2 w-4 h-4" />
                    Book Meeting
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Info & Benefits */}
          <div className="lg:col-span-5 space-y-6">
            {/* Contact Details Card */}
            <div className="card-editorial p-6 animate-on-scroll-right stagger-1">
              <h4 className="text-lg font-bold text-white mb-6">Get In Touch</h4>

              <div className="space-y-5">
                <a href="mailto:ftsupport@fortifytek.com" className="flex items-center gap-4 group">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                    <Mail className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500">Email</p>
                    <p className="text-white font-medium group-hover:text-amber-400 transition-colors">ftsupport@fortifytek.com</p>
                  </div>
                </a>

                <a href="tel:+14242424140" className="flex items-center gap-4 group">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                    <Phone className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500">Phone</p>
                    <p className="text-white font-medium group-hover:text-amber-400 transition-colors">+1 (424) 242-4140</p>
                  </div>
                </a>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500">Location</p>
                    <p className="text-white font-medium">Riverside, CA, USA</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Why Choose Us Card */}
            <div className="card-editorial p-6 animate-on-scroll-right stagger-2">
              <h4 className="text-lg font-bold text-white mb-6">Why Choose FortifyTek?</h4>

              <ul className="space-y-3">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-zinc-400 text-sm">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Response */}
            <div className="glass-panel-warm rounded-2xl p-6 animate-on-scroll-right stagger-3">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                  <svg className="w-7 h-7 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-white font-semibold">Quick Response</p>
                  <p className="text-sm text-zinc-400">We'll get back to you promptly</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
